import { useEffect, useState } from "react";

import { useRouter } from "next/router";

import i18nextConfig from "../../../../next-i18next.config";
import { captureDatadogError } from "../datadog";
import { TranslationFileName } from "../translationFiles";

import { ClientNextI18Next, getLoadedClientTranslations, loadClientTranslations } from "./clientTranslations";

export type ClientTranslationsState =
  | { status: "fromServer" | "loading" | "failed" }
  | { status: "ready"; nextI18Next: ClientNextI18Next };

export const useClientTranslations = (namespaces: TranslationFileName[] | undefined): ClientTranslationsState => {
  const router = useRouter();
  const locale = router.locale ?? i18nextConfig.i18n.defaultLocale;
  const loaded = namespaces ? getLoadedClientTranslations(locale, namespaces) : undefined;
  const [, setLoadedVersion] = useState(0);
  const [failedLocale, setFailedLocale] = useState<string | null>(null);

  // Pages without a server data step load their translations here, once per locale and namespace list.
  useEffect(() => {
    if (!namespaces || loaded) {
      return;
    }
    let isCurrent = true;
    loadClientTranslations(locale, namespaces).then(
      () => {
        if (isCurrent) {
          setLoadedVersion((version) => version + 1);
        }
      },
      (error: unknown) => {
        captureDatadogError({
          message: "Client translations failed to load",
          error,
          extraContext: { locale, namespaces },
        });
        if (isCurrent) {
          setFailedLocale(locale);
        }
      }
    );
    return () => {
      isCurrent = false;
    };
  }, [locale, namespaces, loaded]);

  if (!namespaces) {
    return { status: "fromServer" };
  }
  if (loaded && router.isReady) {
    return { status: "ready", nextI18Next: loaded };
  }
  return { status: failedLocale === locale ? "failed" : "loading" };
};
